import React from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from "@mui/material";
import { resolvePerformanceAmount, toNum } from "./orderManagementUtils";

export default function InvoiceDialog({
    open,
    invoiceOrder,
    invoiceSelections,
    selectablePerformanceCount,
    selectableMaterialCount,
    allPerformanceSelected,
    somePerformanceSelected,
    allMaterialSelected,
    someMaterialSelected,
    hasInvoiceSelections,
    onClose,
    onToggleSelection,
    onToggleAll,
    onConfirm,
    resolveCustomerLabel,
}) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle>Rechnung aus Auftrag</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {invoiceOrder && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {invoiceOrder.title || "Auftrag"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Kunde: {resolveCustomerLabel(invoiceOrder.customerId) || "Unbekannter Kunde"}
                            </Typography>
                        </Box>
                    )}
                    <Divider />
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Leistungen
                            </Typography>
                            {invoiceOrder?.performanceDays?.length > 0 && (
                                <FormControlLabel
                                    disabled={selectablePerformanceCount === 0}
                                    control={
                                        <Checkbox
                                            checked={allPerformanceSelected}
                                            indeterminate={somePerformanceSelected && !allPerformanceSelected}
                                            onChange={(event) => onToggleAll("performance", event.target.checked)}
                                            disabled={selectablePerformanceCount === 0}
                                        />
                                    }
                                    label="Alle"
                                />
                            )}
                        </Stack>
                        <List dense>
                            {invoiceOrder?.performanceDays?.length > 0 ? (
                                invoiceOrder.performanceDays.map((day, index) => (
                                    <ListItem key={`perf-${index}`} dense>
                                        <Checkbox
                                            edge="start"
                                            checked={!!invoiceSelections.performance[index]}
                                            onChange={() => onToggleSelection("performance", index)}
                                            disabled={day.invoiced}
                                            inputProps={{ "aria-label": `Leistung ${index + 1}` }}
                                        />
                                        <ListItemText
                                            primary={`${day.date || "?"} - ${day.articleName || "?"}`}
                                            secondary={`Menge: ${resolvePerformanceAmount(day) || "?"}${
                                                day.invoiced ? " · Bereits abgerechnet" : ""
                                            }`}
                                        />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>
                                    <ListItemText
                                        primary="Keine Leistungen erfasst."
                                        primaryTypographyProps={{ color: "text.secondary" }}
                                    />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                    <Divider />
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Materialien
                            </Typography>
                            {invoiceOrder?.materials?.length > 0 && (
                                <FormControlLabel
                                    disabled={selectableMaterialCount === 0}
                                    control={
                                        <Checkbox
                                            checked={allMaterialSelected}
                                            indeterminate={someMaterialSelected && !allMaterialSelected}
                                            onChange={(event) => onToggleAll("materials", event.target.checked)}
                                            disabled={selectableMaterialCount === 0}
                                        />
                                    }
                                    label="Alle"
                                />
                            )}
                        </Stack>
                        <List dense>
                            {invoiceOrder?.materials?.length > 0 ? (
                                invoiceOrder.materials.map((mat, index) => (
                                    <ListItem key={`mat-${index}`} dense>
                                        <Checkbox
                                            edge="start"
                                            checked={!!invoiceSelections.materials[index]}
                                            onChange={() => onToggleSelection("materials", index)}
                                            disabled={mat.invoiced}
                                            inputProps={{ "aria-label": `Material ${index + 1}` }}
                                        />
                                        <ListItemText
                                            primary={mat.articleName || "?"}
                                            secondary={`Menge: ${toNum(mat.amount) || "?"}${
                                                mat.invoiced ? " · Bereits abgerechnet" : ""
                                            }`}
                                        />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>
                                    <ListItemText
                                        primary="Keine Materialien erfasst."
                                        primaryTypographyProps={{ color: "text.secondary" }}
                                    />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit">
                    Abbrechen
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, color: "#000" }}
                    disabled={!hasInvoiceSelections}
                >
                    Weiter zur Rechnung
                </Button>
            </DialogActions>
        </Dialog>
    );
}
